/**
 * @fileoverview Hook especializado para la gestión de consolidación de mensajes del agente.
 * @module useMessageConsolidation
 * @category Hook - Business Logic / Inbox
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { sharingInformationService } from '../../../services/sharingInformationService';
import {
  DEFAULT_CONSOLIDATION_TIME_SECONDS,
  TARGET_CHAR_LIMIT,
  INTELLIGENT_MAX_CHAR_LIMIT,
  HUMAN_TYPING_PAUSE_THRESHOLD_MS
} from './constants/consolidationConstants';
import { analyzeSemanticIntent } from './utils/consolidationSemanticAnalyzer';

/**
 * Hook para consolidar mensajes seguidos del agente con Inteligencia Semántica y detección de pausa de tipeo.
 *
 * @param {Object} params
 * @param {string|number} params.conversationId - ID de la conversación activa
 * @param {boolean} params.isRuleActive - Indica si la regla de consolidación está activa
 * @param {number} [params.consolidationTimeSeconds=DEFAULT_CONSOLIDATION_TIME_SECONDS] - Tiempo máximo de consolidación en segundos
 * @param {function} params.onFlush - Callback para enviar el texto consolidado al backend
 *
 * @returns {Object} Estado y métodos de consolidación
 */
export default function useMessageConsolidation({
  conversationId,
  isRuleActive = false,
  consolidationTimeSeconds = DEFAULT_CONSOLIDATION_TIME_SECONDS,
  onFlush
}) {
  const [consolidatedText, setConsolidatedText] = useState('');
  const [phraseCount, setPhraseCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  const timerRef = useRef(null);
  const endTimeRef = useRef(null);
  const lastPhraseAddedTimestampRef = useRef(null);
  const adaptivePauseMsRef = useRef(HUMAN_TYPING_PAUSE_THRESHOLD_MS);
  const consolidatedTextRef = useRef('');
  const conversationIdRef = useRef(conversationId);
  const onFlushRef = useRef(onFlush);

  const isTypingInComposerRef = useRef(false);

  // Suscripción al estado de tipeo activo en el composer (caja de texto)
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const subscription = subscription$.subscribe((data) => {
      if (typeof data.isTypingInComposer === 'boolean') {
        isTypingInComposerRef.current = data.isTypingInComposer;
        if (data.isTypingInComposer) {
          lastPhraseAddedTimestampRef.current = Date.now();
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Mantener referencias actualizadas para callbacks asíncronos y desmonte
  useEffect(() => {
    consolidatedTextRef.current = consolidatedText;
  }, [consolidatedText]);

  useEffect(() => {
    onFlushRef.current = onFlush;
  }, [onFlush]);

  useEffect(() => {
    sharingInformationService.setSubject({
      consolidationData: {
        isConsolidating: timeLeft > 0 && consolidatedText.length > 0,
        timeLeft,
        phraseCount,
        consolidatedText
      }
    });
  }, [timeLeft, phraseCount, consolidatedText]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    clearTimer();
    endTimeRef.current = null;
    lastPhraseAddedTimestampRef.current = null;
    isTypingInComposerRef.current = false;
    adaptivePauseMsRef.current = HUMAN_TYPING_PAUSE_THRESHOLD_MS;
    setConsolidatedText('');
    setPhraseCount(0);
    setTimeLeft(0);
    consolidatedTextRef.current = '';
  }, [clearTimer]);

  /**
   * Envía inmediatamente al backend cualquier texto consolidado pendiente.
   */
  const flushConsolidatedMessage = useCallback(() => {
    const textToSend = consolidatedTextRef.current.trim();
    const targetConvId = conversationIdRef.current;
    if (textToSend && typeof onFlushRef.current === 'function') {
      onFlushRef.current(textToSend, targetConvId);
    }
    resetState();
  }, [resetState]);

  // Manejo de cambio de conversación: enviar pendiente antes de cambiar de chat
  useEffect(() => {
    if (conversationIdRef.current && conversationIdRef.current !== conversationId) {
      if (consolidatedTextRef.current.trim()) {
        flushConsolidatedMessage();
      } else {
        resetState();
      }
    }
    conversationIdRef.current = conversationId;
  }, [conversationId, flushConsolidatedMessage, resetState]);

  // Manejo de desmonte y salida de ventana
  useEffect(() => {
    const handleVisibilityOrUnload = () => {
      if (document.hidden && consolidatedTextRef.current.trim()) {
        flushConsolidatedMessage();
      }
    };

    window.addEventListener('beforeunload', flushConsolidatedMessage);
    document.addEventListener('visibilitychange', handleVisibilityOrUnload);

    return () => {
      window.removeEventListener('beforeunload', flushConsolidatedMessage);
      document.removeEventListener('visibilitychange', handleVisibilityOrUnload);
      if (consolidatedTextRef.current.trim()) {
        flushConsolidatedMessage();
      } else {
        clearTimer();
      }
    };
  }, [flushConsolidatedMessage, clearTimer]);

  // Temporizador inteligente adaptativo por inactividad de tipeo y tiempo límite
  useEffect(() => {
    if (!phraseCount || !endTimeRef.current) return;

    clearTimer();
    timerRef.current = setInterval(() => {
      if (!endTimeRef.current) {
        clearTimer();
        return;
      }
      const now = Date.now();

      // Si el agente está redactando activamente en la caja de texto (ej. escribiendo una frase de 50 caracteres),
      // mantener actualizada la referencia de tipeo y NO enviar prematuramente.
      if (isTypingInComposerRef.current) {
        lastPhraseAddedTimestampRef.current = now;
        setTimeLeft(consolidationTimeSeconds);
        return;
      }

      const remainingMs = endTimeRef.current - now;
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

      const timeSinceLastPhraseMs = now - (lastPhraseAddedTimestampRef.current || now);
      const targetPauseMs = adaptivePauseMsRef.current || HUMAN_TYPING_PAUSE_THRESHOLD_MS;
      const isTypingPauseReached = timeSinceLastPhraseMs >= targetPauseMs;

      if (remainingSec <= 0 || isTypingPauseReached) {
        clearTimer();
        setTimeLeft(0);
        flushConsolidatedMessage();
      } else {
        setTimeLeft(remainingSec);
      }
    }, 200);

    return () => {
      clearTimer();
    };
  }, [phraseCount, consolidationTimeSeconds, clearTimer, flushConsolidatedMessage]);

  /**
   * Intenta procesar un nuevo mensaje mediante patrones de Inteligencia Artificial Semántica.
   * Analiza completitud, conectores abiertos, saludos e intención antes de decidir consolidar o enviar.
   *
   * @param {string} newText - Texto ingresado por el agente
   * @returns {boolean} True si el mensaje fue interceptado/consolidado, False si debe enviarse de inmediato.
   */
  const handleIncomingText = useCallback((newText) => {
    isTypingInComposerRef.current = false;

    if (!isRuleActive) return false;

    const trimmed = (newText || '').trim();
    if (!trimmed) return false;

    const currentBuffer = consolidatedTextRef.current;
    const semantics = analyzeSemanticIntent(trimmed);

    // Patrón IA: Si es una idea larga semánticamente terminada y sin conectores abiertos, se envía de inmediato.
    if (semantics.isLongCompleteSentence) {
      if (currentBuffer.trim()) {
        flushConsolidatedMessage();
      }
      return false;
    }

    const candidate = currentBuffer ? `${currentBuffer}\n${trimmed}` : trimmed;

    // Patrón IA de límite y acumulación inteligente
    if (candidate.length <= INTELLIGENT_MAX_CHAR_LIMIT) {
      if (candidate.length >= TARGET_CHAR_LIMIT) {
        if (typeof onFlushRef.current === 'function') {
          onFlushRef.current(candidate);
        }
        resetState();
        return true;
      }

      setConsolidatedText(candidate);
      consolidatedTextRef.current = candidate;
      setPhraseCount((prev) => (currentBuffer ? prev + 1 : 1));
      lastPhraseAddedTimestampRef.current = Date.now();
      adaptivePauseMsRef.current = semantics.adaptivePauseMs;
      endTimeRef.current = Date.now() + consolidationTimeSeconds * 1000;
      setTimeLeft(consolidationTimeSeconds);
      return true;
    }

    if (currentBuffer.trim() && typeof onFlushRef.current === 'function') {
      onFlushRef.current(currentBuffer.trim());
    }

    setConsolidatedText(trimmed);
    consolidatedTextRef.current = trimmed;
    setPhraseCount(1);
    lastPhraseAddedTimestampRef.current = Date.now();
    adaptivePauseMsRef.current = semantics.adaptivePauseMs;
    endTimeRef.current = Date.now() + consolidationTimeSeconds * 1000;
    setTimeLeft(consolidationTimeSeconds);
    return true;
  }, [isRuleActive, consolidationTimeSeconds, resetState, flushConsolidatedMessage]);

  return {
    consolidatedText,
    phraseCount,
    timeLeft,
    isConsolidating: timeLeft > 0 && consolidatedText.length > 0,
    handleIncomingText,
    flushConsolidatedMessage,
    resetState
  };
}
